import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Plus, Trash2, Mail, Clock } from 'lucide-react';

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ patient_id: '', clinician_id: '', notes: '', preferred_window: '' });
  const [status, setStatus] = useState('open');

  const load = (s = status) => api.get('/waitlist', { params: { status: s } }).then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/patients').then(r => setPatients(r.data || []));
    api.get('/clinicians').then(r => setClinicians(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.patient_id) return;
    await api.post('/waitlist', form);
    setShow(false); setForm({ patient_id: '', clinician_id: '', notes: '', preferred_window: '' });
    load();
  };
  const remove = async (wid) => { await api.delete(`/waitlist/${wid}`); load(); };
  const markBooked = async (wid) => { await api.put(`/waitlist/${wid}`, { status: 'booked' }); load(); };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-waitlist-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">SCHEDULING</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Waitlist</h1>
          <p className="text-fg-muted mt-2 text-sm">When a patient cancels, the oldest matching entry below is automatically emailed.</p>
        </div>
        <button onClick={()=>setShow(s=>!s)} className="btn-primary inline-flex items-center gap-2" data-testid="add-waitlist-btn">
          <Plus className="w-4 h-4"/> {show ? 'Close' : 'Add'}
        </button>
      </div>

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-3">Add to waitlist</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Patient">
              <select className="input" value={form.patient_id} onChange={e=>setForm({...form, patient_id: e.target.value})} data-testid="wl-patient">
                <option value="">Select…</option>
                {patients.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name}</option>)}
              </select>
            </Field>
            <Field label="Clinician (optional — leave blank to match any)">
              <select className="input" value={form.clinician_id} onChange={e=>setForm({...form, clinician_id: e.target.value})} data-testid="wl-clinician">
                <option value="">Any</option>
                {clinicians.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Preferred window">
              <input className="input" value={form.preferred_window} onChange={e=>setForm({...form, preferred_window: e.target.value})}
                     placeholder="e.g. afternoons, weekends" data-testid="wl-window"/>
            </Field>
            <Field label="Notes">
              <input className="input" value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} data-testid="wl-notes"/>
            </Field>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={add} className="btn-primary" disabled={!form.patient_id} data-testid="wl-save">Add</button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-3">
        {['open', 'notified', 'booked', 'removed'].map(s => (
          <button key={s} onClick={()=>{ setStatus(s); load(s); }}
                  className={`px-3 py-1 rounded-md text-xs font-medium ${status===s ? 'bg-brand text-white' : 'surface border border-soft text-fg-muted'}`}
                  data-testid={`wl-tab-${s}`}>
            {s.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="bento-card">
        {items.length === 0 ? (
          <div className="text-fg-muted text-sm py-6 text-center" data-testid="wl-empty">No entries here.</div>
        ) : (
          <table>
            <thead><tr><th>Patient</th><th>Clinician</th><th>Window</th><th>Added</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map(w => (
                <tr key={w.waitlist_id} data-testid={`wl-row-${w.waitlist_id}`}>
                  <td>{w.patient_name}</td>
                  <td className="text-xs text-fg-muted">{w.clinician_id ? clinicians.find(c=>c.user_id===w.clinician_id)?.name || w.clinician_id.slice(0,8) : 'Any'}</td>
                  <td className="text-xs">{w.preferred_window || '—'}</td>
                  <td className="text-xs text-fg-muted">{(w.created_at || '').slice(0,10)}</td>
                  <td>
                    {w.status === 'notified' ? (
                      <span className="badge-warn inline-flex items-center gap-1" title={`Notified about ${w.notified_slot || ''}`}>
                        <Mail className="w-3 h-3"/> NOTIFIED
                      </span>
                    ) : (
                      <span className="badge-neutral">{w.status?.toUpperCase()}</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {w.status === 'notified' && (
                        <button onClick={()=>markBooked(w.waitlist_id)} className="text-xs text-brand" data-testid={`wl-booked-${w.waitlist_id}`}>Mark booked</button>
                      )}
                      <button onClick={()=>remove(w.waitlist_id)} className="text-[#B83A3A]" data-testid={`wl-del-${w.waitlist_id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-3 text-[10px] text-fg-muted flex items-center gap-1">
        <Clock className="w-3 h-3"/> Entries are matched FIFO. When an appointment is cancelled, the oldest matching entry (clinician=specific or Any) is auto-emailed and moved to NOTIFIED.
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
